import { allElementsAreEqual } from "helpers/array-helpers";
import { Worklist } from "../shared/interfaces";
import {
  extractPlateCode,
  getColIndexFromSelectedWell,
  getPlateWellIdFromRowAndColIndex,
  getRowIndexFromSelectedWell,
} from "../shared/WorklistHelpers";
import {
  DestPlateInfoState,
  IntermediatePlateInfoState,
  IStampTopLeftWorklistState,
  PoolingNormalizationToolActions,
  PoolingNormalizationToolState,
  SourcePlateInfoState,
  TemplateWellMappingIndexesToDelete,
  WorklistValuesState,
  WorklistWellMappingState,
} from "./state";
import {
  IPoolingMethodSettings,
  IPoolingNormalizationToolInternalState,
} from "./state/initial-state";

export enum CellNumberUpdateType {
  ALL = "All",
  EMPTY = "Empty",
  INDIVIDUAL = "Individual",
}

export const containsCrossContamination = (
  worklistValues: WorklistValuesState
) => {
  //check if all source wells are equal in each intermediate stamp
  const stampTopLeftKeys = [
    "int1StampTopLeft",
    "int2StampTopLeft",
    "int3StampTopLeft",
  ];
  const worklistKeys = Object.keys(worklistValues);
  for (const key of worklistKeys) {
    if (!stampTopLeftKeys.includes(key)) continue;

    const extractSourceWells = (entry: IStampTopLeftWorklistState[]) => {
      return entry.map((well) => {
        return well.sourceWellId;
      });
    };

    const stampedSourceWells = extractSourceWells(
      worklistValues[key as keyof WorklistValuesState]
    );

    if (stampedSourceWells.length && !allElementsAreEqual(stampedSourceWells)) {
      return true;
    }
  }

  return false;
};

export const containsStampAndSingleChannelSeeding = (worklistValues: any) => {
  return (
    (worklistValues.int1ToDest.length ||
      worklistValues.int2ToDest.length ||
      worklistValues.int3ToDest.length) &&
    (worklistValues.int1StampTopLeft.length ||
      worklistValues.int2StampTopLeft.length ||
      worklistValues.int3StampTopLeft.length)
  );
};

export const containsStamp = (worklistValues: WorklistValuesState) => {
  return (
    worklistValues.int1StampTopLeft.length ||
    worklistValues.int2StampTopLeft.length ||
    worklistValues.int3StampTopLeft.length
  );
};

export const containsSingleChannelSeeding = (
  worklistValues: WorklistValuesState
) => {
  return (
    worklistValues.int1ToDest.length ||
    worklistValues.int2ToDest.length ||
    worklistValues.int3ToDest.length
  );
};

export const getWorklistRows = (worklistValues: any, destIntIndex: number) => {
  const worklistRows = [];
  switch (destIntIndex) {
    case 1:
      return worklistValues.int1ToInt2;
    case 2:
      let worklistRows = [];
      worklistRows = [
        ...worklistValues.int1ToInt3,
        ...worklistValues.int2ToInt3,
      ];
      return worklistRows;
    default:
      return worklistValues.harvestWells;
  }
};

export const buildHarvestWellsWorklist = (
  harvestWells: any[],
  sourcePlateInfo: any[],
  destPlateInfo: any[],
  methodSettings: any
) => {
  const worklist: any[] = [];
  for (const row of harvestWells) {
    const currentSourcePlate = sourcePlateInfo[row.sourcePlateIndex];
    const currentDestPlate = destPlateInfo[row.destPlateIndex];
    const index = currentSourcePlate.wellInfo.findIndex(
      (e: any) =>
        e.rowPos - 1 === getRowIndexFromSelectedWell(row.sourceWellId) &&
        e.colPos - 1 === getColIndexFromSelectedWell(row.sourceWellId)
    );
    if (index > -1)
      worklist.push({
        task: "HarvestWells",
        details: "",
        aspirateVol: "",
        dispenseVol: "",
        transferVol: row.transferVol.toString(),
        sourcePlateType: currentSourcePlate.labwareTypeCode,
        sourcePlateBarcode: currentSourcePlate.plateBarcode,
        sourceWellID: row.sourceWellId,
        destinationPlateType: currentDestPlate.labwareTypeCode,
        destinationPlateBarcode: currentDestPlate.plateBarcode,
        destinationWellID: row.destWellId,
      });
  }
  return worklist;
};

export const buildInt1ToInt2Worklist = (
  int1ToInt2: any[],
  sourcePlateInfo: any[],
  intPlateInfo: any[],
  destPlateInfo: any[],
  methodSettings: any
) => {
  const worklist: any[] = [];

  for (const row of int1ToInt2) {
    worklist.push({
      task: "Int1ToInt2",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: row.transferVol.toString(),
      sourcePlateType: intPlateInfo[0].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[0].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: intPlateInfo[1].labwareTypeCode,
      destinationPlateBarcode: intPlateInfo[1].plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt2ToInt3Worklist = (
  int1ToInt2: any[],
  sourcePlateInfo: any[],
  intPlateInfo: any[],
  destPlateInfo: any[],
  methodSettings: any
) => {
  const worklist: any[] = [];

  for (const row of int1ToInt2) {
    worklist.push({
      task: "Int2ToInt3",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: row.transferVol.toString(),
      sourcePlateType: intPlateInfo[1].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[1].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: intPlateInfo[2].labwareTypeCode,
      destinationPlateBarcode: intPlateInfo[2].plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt1ToInt3Worklist = (
  int1ToInt3: any[],
  sourcePlateInfo: any[],
  intPlateInfo: any,
  destPlateInfo: any[],
  methodSettings: any
) => {
  const worklist: any[] = [];

  for (const row of int1ToInt3) {
    worklist.push({
      task: "Int1ToInt3",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: row.transferVol.toString(),
      sourcePlateType: intPlateInfo[0].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[0].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: intPlateInfo[2].labwareTypeCode,
      destinationPlateBarcode: intPlateInfo[2].plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt1StampTopLeftWorklist = (
  int1StampTopLeft: any[],
  intPlateInfo: any[],
  destPlateInfo: any[]
) => {
  const worklist: any[] = [];
  for (const row of int1StampTopLeft) {
    const currentDestPlateInfo = destPlateInfo[row.destPlateIndex];
    worklist.push({
      task: "Int1StampTopLeft",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: "",
      sourcePlateType: intPlateInfo[0].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[0].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: currentDestPlateInfo.labwareTypeCode,
      destinationPlateBarcode: currentDestPlateInfo.plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt2StampTopLeftWorklist = (
  int2StampTopLeft: any[],
  intPlateInfo: any[],
  destPlateInfo: any[]
) => {
  const worklist: any[] = [];
  for (const row of int2StampTopLeft) {
    const currentDestPlateInfo = destPlateInfo[row.destPlateIndex];
    worklist.push({
      task: "Int2StampTopLeft",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: "",
      sourcePlateType: intPlateInfo[1].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[1].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: currentDestPlateInfo.labwareTypeCode,
      destinationPlateBarcode: currentDestPlateInfo.plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt3StampTopLeftWorklist = (
  int3StampTopLeft: any[],
  intPlateInfo: any[],
  destPlateInfo: any[]
) => {
  const worklist: any[] = [];
  for (const row of int3StampTopLeft) {
    const currentDestPlateInfo = destPlateInfo[row.destPlateIndex];
    worklist.push({
      task: "Int3StampTopLeft",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: "",
      sourcePlateType: intPlateInfo[2].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[2].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: currentDestPlateInfo.labwareTypeCode,
      destinationPlateBarcode: currentDestPlateInfo.plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt1ToDestWorklist = (
  int1ToDest: any[],
  sourcePlateInfo: any[],
  intPlateInfo: any,
  destPlateInfo: any[],
  methodSettings: any
) => {
  const worklist: any[] = [];

  for (const row of int1ToDest) {
    const destPlateIndex = destPlateInfo.findIndex(
      (item) => item.index == row.destPlateIndex
    );
    worklist.push({
      task: "Int1ToDest",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: row.transferVol.toString(),
      sourcePlateType: intPlateInfo[0].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[0].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: destPlateInfo[destPlateIndex].labwareTypeCode,
      destinationPlateBarcode: destPlateInfo[destPlateIndex].plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt2ToDestWorklist = (
  int2ToDest: any[],
  sourcePlateInfo: any[],
  intPlateInfo: any,
  destPlateInfo: any[],
  methodSettings: any
) => {
  const worklist: any[] = [];

  for (const row of int2ToDest) {
    const destPlateIndex = destPlateInfo.findIndex(
      (item) => item.index == row.destPlateIndex
    );
    worklist.push({
      task: "Int2ToDest",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: row.transferVol.toString(),
      sourcePlateType: intPlateInfo[1].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[1].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: destPlateInfo[destPlateIndex].labwareTypeCode,
      destinationPlateBarcode: destPlateInfo[destPlateIndex].plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildInt3ToDestWorklist = (
  int3ToDest: any[],
  sourcePlateInfo: any[],
  intPlateInfo: any,
  destPlateInfo: any[],
  methodSettings: any
) => {
  const worklist: any[] = [];

  for (const row of int3ToDest) {
    const destPlateIndex = destPlateInfo.findIndex(
      (item) => item.index == row.destPlateIndex
    );
    worklist.push({
      task: "Int3ToDest",
      details: "",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: row.transferVol.toString(),
      sourcePlateType: intPlateInfo[2].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[2].plateBarcode,
      sourceWellID: row.sourceWellId,
      destinationPlateType: destPlateInfo[destPlateIndex].labwareTypeCode,
      destinationPlateBarcode: destPlateInfo[destPlateIndex].plateBarcode,
      destinationWellID: row.destWellId,
    });
  }
  return worklist;
};

export const buildReferenceStampShape = (stampWells: string[]) => {
  const worklist: Worklist[] = [];
  const isConsecutive = (currentWellId: string, nextWellId: string) => {
    let isSameRow = false;
    let isSameCol = false;
    if (currentWellId.charCodeAt(0) === nextWellId.charCodeAt(0))
      isSameRow = true;
    if (
      parseInt(nextWellId.substring(1)) -
        parseInt(currentWellId.substring(1)) ===
      1
    )
      isSameCol = true;
    return isSameRow && isSameCol;
  };

  let referenceStampShape = "";
  let isCountingConsecutives = false;

  for (let i = 0; i < stampWells.length; i++) {
    if (i + 1 > stampWells.length - 1) {
      referenceStampShape += `${stampWells[i]};`;
      continue;
    }
    const wellsAreConsecutive = isConsecutive(stampWells[i], stampWells[i + 1]);
    if (wellsAreConsecutive && !isCountingConsecutives) {
      referenceStampShape += `${stampWells[i]}:`;
      isCountingConsecutives = true;
    } else if (wellsAreConsecutive) {
      continue;
    } else {
      isCountingConsecutives = false;
      referenceStampShape += `${stampWells[i]};`;
    }
  }
  if (stampWells.length > 0) {
    worklist.push({
      task: "ReferenceStampShape",
      sourceWellID: referenceStampShape,
    });
  }

  return worklist;
};

export const buildOperatingVolumeWorklist = (
  sourcePlateInfo: SourcePlateInfoState[],
  destPlateInfo: DestPlateInfoState[]
) => {
  const worklist: Worklist[] = [];
  for (let i = 0; i < sourcePlateInfo.length; i++) {
    if (sourcePlateInfo[i].plateBarcode != "") {
      worklist.push({
        task: "SourceLabwareOperating",
        details: sourcePlateInfo[i].operatingVol.toString() ?? "",
        destinationPlateType: sourcePlateInfo[i].labwareTypeCode,
      });
    }
  }

  for (let i = 0; i < destPlateInfo.length; i++) {
    if (destPlateInfo[i].plateBarcode != "") {
      worklist.push({
        task: "DestinationLabwareOperating",
        details: destPlateInfo[i].operatingVol.toString(),
        aspirateVol: (+destPlateInfo[i].preprocess).toString(),
        dispenseVol: (+destPlateInfo[i].topup).toString(),
        transferVol: destPlateInfo[i].startingVol.toString(),
        destinationPlateType: destPlateInfo[i].labwareTypeCode,
        destinationPlateBarcode: destPlateInfo[i].plateBarcode,
      });
    }
  }
  return worklist;
};

export const buildDiscardSourcePlateWorklist = (
  sourcePlateInfo: SourcePlateInfoState[]
) => {
  const worklist: Worklist[] = [];
  for (let i = 0; i < sourcePlateInfo.length; i++) {
    if (sourcePlateInfo[i].plateBarcode !== "") {
      worklist.push({
        task: "DiscardSourcePlate",
        details: sourcePlateInfo[i].discardSourcePlate.toString() ?? "",
        sourcePlateType: sourcePlateInfo[i].labwareTypeCode,
        sourcePlateBarcode: sourcePlateInfo[i].plateBarcode,
      });
    }
  }
  return worklist;
};

export const buildSeedingPriorityWorklist = (
  groupedSeedingPriority: { id: string; content: string }[],
  seedingPriority_12_24_Well: string[],
  seedingPriority_96_384_Well: string[]
) => {
  const worklist: Worklist[] = [];
  let priorityValue = 1;

  for (const row of groupedSeedingPriority) {
    if (row.id === "12/24W") {
      for (const labwareTypeCode of seedingPriority_12_24_Well) {
        worklist.push({
          task: "SeedingPriority",
          details: priorityValue.toString(),
          aspirateVol: "0",
          destinationPlateType: labwareTypeCode,
        });
        priorityValue++;
      }
    } else if (row.id === "96/384W") {
      for (const labwareTypeCode of seedingPriority_96_384_Well) {
        worklist.push({
          task: "SeedingPriority",
          details: priorityValue.toString(),
          aspirateVol: "0",
          destinationPlateType: labwareTypeCode,
        });
        priorityValue++;
      }
    }
  }
  return worklist;
};

export const buildMethodSettingsWorklist = (
  poolingState: IPoolingNormalizationToolInternalState,
  buildToDownload = true
) => {
  const methodSettings = poolingState.methodSettings;
  const worklist: Worklist[] = [];

  worklist.push({
    task: "IncubationTime",
    details: buildToDownload
      ? (methodSettings.dissociationTime * 60).toString()
      : methodSettings.dissociationTime.toString(),
  });
  worklist.push({
    task: "SpinParameters",
    details: methodSettings.spinParamPercent.toString(),
    aspirateVol: buildToDownload
      ? (methodSettings.spinTime * 60).toString()
      : methodSettings.spinTime.toString(),
    dispenseVol: methodSettings.spinAccel.toString(),
    transferVol: methodSettings.spinDecel.toString(),
  });
  // Remove comments to add Source96HeadProcessing Logic
  // worklist.push({
  //   task: "Source96HeadProcessing",
  //   details: (+methodSettings.source96HeadProcessing).toString(),
  // });
  worklist.push({
    task: "Int196HeadProcessing",
    details: (+methodSettings.int196HeadProcessing).toString(),
  });
  worklist.push({
    task: "ReducedVolWellWash",
    details: (+methodSettings.reducedVolWellWash).toString(),
  });
  worklist.push({
    task: "ReducedPelResusSpeed",
    details: (+methodSettings.reducedPelResusSpeed).toString(),
  });
  worklist.push({
    task: "ExtraPelletWash",
    details: (+methodSettings.extraPelletWash).toString(),
  });
  worklist.push({
    task: "Dissociation",
    details: (+methodSettings.dissociation).toString(),
  });
  worklist.push({
    task: "Harvest",
    details: (+methodSettings.harvest).toString(),
  });
  worklist.push({
    task: "DissociationWash",
    details: (+methodSettings.washBeforeDissociation).toString(),
  });
  worklist.push({
    task: "DissociationWashRGT",
    details: methodSettings.dissociationWashRGT.toString(),
  });
  worklist.push({
    task: "HarvestWash",
    details: (+methodSettings.washAfterDissociation).toString(),
  });
  worklist.push({
    task: "HarvestWashRGT",
    details: methodSettings.harvestWashRGT.toString(),
  });
  worklist.push({
    task: "ReFeedWells",
    details: (+methodSettings.reFeedSourceWells).toString(),
  });
  worklist.push({
    task: "ReFeedWellsRGT",
    details: methodSettings.reFeedWellsRGT.toString(),
  });
  worklist.push({
    task: "Int1SpinResuspend",
    details: methodSettings.int1SpinResuspend.toString(),
  });
  worklist.push({
    task: "Int1CountAliquot",
    details: methodSettings.int1CountAliquot.toString(),
  });
  worklist.push({
    task: "StampReuseTips",
    details: (+(methodSettings.stampReuseTips ?? false)).toString(),
  });
  worklist.push({
    task: "StampVolume",
    details: methodSettings.stampVolume.toString(),
  });
  return worklist;
};

export const buildAuxPlateBarcodeWorklist = (
  intPlateInfo: IntermediatePlateInfoState[],
  deadPlateBarcode: string,
  deadPlateType: string,
  methodSettings: IPoolingMethodSettings
) => {
  const worklist: Worklist[] = [];
  for (const [index, plate] of intPlateInfo.entries()) {
    if (plate.labwareTypeCode !== "") {
      worklist.push({
        task: `Intermediate${index + 1}Resuspension`,
        details: plate.resuspensionVol.toString(),
        sourcePlateBarcode: plate.plateBarcode,
      });
    }
  }
  worklist.push({
    task: "DeadTotalPlate",
    details: methodSettings.numberOfAliquots.toString(),
    transferVol: methodSettings.aliquotVolume.toString(),
    sourcePlateType: deadPlateType,
    sourcePlateBarcode: deadPlateBarcode,
  });

  return worklist;
};

export const buildMappingFileWorklist = (
  sourcePlateInfo: SourcePlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[],
  destPlateInfo: DestPlateInfoState[]
) => {
  const worklist: Worklist[] = [];
  const uniqueSourcePlateCodes = [
    ...new Set(
      sourcePlateInfo
        .map((e) => extractPlateCode(e.plateBarcode))
        .filter((labware) => labware !== "")
    ),
  ];
  const uniqueDestPlateCodes = [
    ...new Set(
      destPlateInfo
        .map((e) => extractPlateCode(e.plateBarcode))
        .filter((labware) => labware !== "")
    ),
  ];

  const containsIntermediate2Plate = intPlateInfo[1].plateBarcode !== "";
  const containsIntermediate3Plate = intPlateInfo[2].plateBarcode !== "";

  const srcPlateCode =
    uniqueSourcePlateCodes.length > 1 ? "X" : uniqueSourcePlateCodes[0];
  const destPlateCode =
    uniqueDestPlateCodes.length > 1 ? "Y" : uniqueDestPlateCodes[0];
  //TODO: figure out how to handle multiple int plate types
  const intPlateCode = "Int" + srcPlateCode + "-" + destPlateCode;
  const deadPlateCode = "Dead" + srcPlateCode + "-" + destPlateCode;
  worklist.push({
    task: "SrcToIntMappingFileKeyword",
    sourcePlateBarcode: "_" + srcPlateCode + "_to_" + intPlateCode + "_",
  });
  if (containsIntermediate2Plate) {
    worklist.push({
      task: "Int1ToInt2MappingFileKeyword",
      sourcePlateBarcode: "_" + intPlateCode + "_to_" + intPlateCode + "_",
    });
  }
  if (containsIntermediate3Plate) {
    worklist.push({
      task: "Int12ToInt3MappingFileKeyword",
      sourcePlateBarcode: "_" + intPlateCode + "_to_" + intPlateCode + "_",
    });
  }
  worklist.push({
    task: "IntToDeadMappingFileKeyword",
    sourcePlateBarcode: "_" + intPlateCode + "_to_" + deadPlateCode + "_",
  });
  worklist.push({
    task: "IntToDestMappingFileKeyword",
    sourcePlateBarcode: "_" + intPlateCode + "_to_" + destPlateCode + "_",
  });

  return worklist;
};

const findNextPlateMissingWells = (
  previousPlateMissingWellIds: Set<string>,
  nextPlateMap: WorklistWellMappingState[]
): {
  nextIndexesToDelete: number[];
  nextMissingWellIds: Set<string>;
} => {
  const nextIndexesToDelete: number[] = [];
  const nextMissingWellIds: Set<string> = new Set();

  //if no prev missing wells return
  if (previousPlateMissingWellIds.size === 0 || nextPlateMap.length === 0) {
    return { nextIndexesToDelete, nextMissingWellIds };
  }

  //check if prev missing well on next plate
  nextPlateMap.forEach((nextPlateMapping, index) => {
    if (previousPlateMissingWellIds.has(nextPlateMapping.sourceWellId)) {
      nextIndexesToDelete.push(index);
      nextMissingWellIds.add(nextPlateMapping.destWellId);
    }
  });

  return { nextIndexesToDelete, nextMissingWellIds };
};

//Populates deletion index arrays by tracing missing int1 wells downstream.
const populateIndexesToDelete = (
  wellMappings: WorklistValuesState,
  missingInt1WellIds: Set<string>,
  indexesToDelete: TemplateWellMappingIndexesToDelete
): TemplateWellMappingIndexesToDelete => {
  // int1 -> dest
  const int1ToDest = findNextPlateMissingWells(
    missingInt1WellIds,
    wellMappings.int1ToDest
  );
  indexesToDelete.int1ToDest.push(...int1ToDest.nextIndexesToDelete);

  // int1 -> int2 -> dest
  const int1ToInt2 = findNextPlateMissingWells(
    missingInt1WellIds,
    wellMappings.int1ToInt2
  );
  indexesToDelete.int1ToInt2.push(...int1ToInt2.nextIndexesToDelete);

  const missingInt2WellIds = int1ToInt2.nextMissingWellIds;

  const int2ToDest = findNextPlateMissingWells(
    missingInt2WellIds,
    wellMappings.int2ToDest
  );
  indexesToDelete.int2ToDest.push(...int2ToDest.nextIndexesToDelete);

  // int1 -> int2 -> int3 -> dest
  const int2ToInt3 = findNextPlateMissingWells(
    missingInt2WellIds, //Re-used from above
    wellMappings.int2ToInt3
  );
  indexesToDelete.int2ToInt3.push(...int2ToInt3.nextIndexesToDelete);

  const missingInt3WellIdsFromInt2 = int2ToInt3.nextMissingWellIds;

  const int3ToDest_fromInt2 = findNextPlateMissingWells(
    missingInt3WellIdsFromInt2,
    wellMappings.int3ToDest
  );
  indexesToDelete.int3ToDest.push(...int3ToDest_fromInt2.nextIndexesToDelete);

  // int1 -> int3 -> dest
  const int1ToInt3 = findNextPlateMissingWells(
    missingInt1WellIds,
    wellMappings.int1ToInt3
  );
  indexesToDelete.int1ToInt3.push(...int1ToInt3.nextIndexesToDelete);

  const missingInt3WellIdsFromInt1 = int1ToInt3.nextMissingWellIds;

  const int3ToDest_fromInt1 = findNextPlateMissingWells(
    missingInt3WellIdsFromInt1,
    wellMappings.int3ToDest
  );
  indexesToDelete.int3ToDest.push(...int3ToDest_fromInt1.nextIndexesToDelete);

  return indexesToDelete;
};

export async function deleteMissingTemplateWellIds(
  poolingState: PoolingNormalizationToolState,
  dispatch: (action: { type: string; payload: any }) => void,
  isConfirmed: (prompt: string, bullets?: string[]) => Promise<boolean>
) {
  const indexesToDelete: TemplateWellMappingIndexesToDelete = {
    harvestWells: [],
    int1ToInt2: [],
    int1ToInt3: [],
    int2ToInt3: [],
    int1ToDest: [],
    int2ToDest: [],
    int3ToDest: [],
  };

  const populatedSourcePlates = poolingState.sourcePlateInfo.filter(
    (srcPlate: SourcePlateInfoState) =>
      srcPlate.wellInfo.length > 0 && srcPlate.plateBarcode !== ""
  );

  const wellMappings = poolingState.worklistValues;

  if (wellMappings.harvestWells.length === 0) return;

  const missingHarvestWells: WorklistWellMappingState[] = [];

  for (const [
    sourcePlateIndex,
    sourcePlate,
  ] of populatedSourcePlates.entries()) {
    const srcWellIds = new Set(
      sourcePlate.wellInfo.map((wellInfo: any) => {
        return getPlateWellIdFromRowAndColIndex(
          wellInfo.rowPos - 1,
          wellInfo.colPos - 1
        );
      })
    );

    //Check harvest wells against each source plate
    for (const [index, harvestWell] of wellMappings.harvestWells.entries()) {
      if (
        !srcWellIds.has(harvestWell.sourceWellId) &&
        harvestWell.sourcePlateIndex === sourcePlateIndex //only check current src plate in loop
      ) {
        missingHarvestWells.push(harvestWell);
        indexesToDelete.harvestWells.push(index);
      }
    }
  }

  if (missingHarvestWells.length === 0) return;

  const customStampingPresent =
    wellMappings.int1StampTopLeft.length > 0 ||
    wellMappings.int2StampTopLeft.length > 0 ||
    wellMappings.int3StampTopLeft.length > 0;

  if (customStampingPresent) {
    await isConfirmed("Source Plate Issue", [
      "Source plate is not compatible with the custom stamp shape and downstream mapping defined by this template.",
      "Create worklist without using a template or use source plate that matches template.",
    ]);

    //TODO: Option to delete stamping mappings only?
    dispatch(PoolingNormalizationToolActions.RESET_STATE());
    return;
  }

  const missingInt1WellIds = new Set(
    missingHarvestWells.map((m) => m.destWellId)
  );

  populateIndexesToDelete(wellMappings, missingInt1WellIds, indexesToDelete);

  await isConfirmed("Source Plate Issue", [
    "Source plate is missing wells that are present in your template. Any wellmappings connected to missing wells have been deleted.",
    "Check Int and Dest plates to makes sure they still meet your needs.",
  ]);

  dispatch(
    PoolingNormalizationToolActions.CLEAR_MISSING_TEMPLATE_WELLS(
      indexesToDelete
    )
  );
}

export const getFullMappingFromSourceToDestinationPlate = (
  worklistValues: WorklistValuesState,
  sourcePlateIndex: number,
  sourceWellId: string
) => {
  const harvestWell = worklistValues.harvestWells.filter(
    (e) =>
      e.sourcePlateIndex === sourcePlateIndex && e.sourceWellId === sourceWellId
  );

  if (harvestWell.length === 0) {
    return;
  }

  const matches = (
    from: WorklistWellMappingState,
    to: WorklistWellMappingState
  ) =>
    from.destPlateIndex === to.sourcePlateIndex &&
    from.destWellId === to.sourceWellId;

  const intWells = [
    ...worklistValues.int1ToInt2,
    ...worklistValues.int1ToInt3,
    ...worklistValues.int2ToInt3,
  ];

  const allIntWells = intWells.filter((intWell) =>
    matches(harvestWell[0], intWell)
  );

  const stampTopLeftTransfers = worklistValues.stampTopLeftTransfers.filter(
    (stampWell) =>
      allIntWells.length > 0
        ? allIntWells.some((intWell) => matches(intWell, stampWell))
        : harvestWell.some((intWell) => matches(intWell, stampWell))
  );


  const destWells = [
    ...worklistValues.int1ToDest,
    ...worklistValues.int2ToDest,
    ...worklistValues.int3ToDest,
  ];
  const allDestWells = destWells.filter((destWell) =>
    allIntWells.length > 0
      ? allIntWells.some((intWell) => matches(intWell, destWell))
      : harvestWell.some((intWell) => matches(intWell, destWell))
  );

  const wellMappings = {
    harvestWells: harvestWell,
    intWells: allIntWells,
    destWells: allDestWells,
    stampTopLeftTransfers: stampTopLeftTransfers,
  };
  return wellMappings;
};

export const collectWellsToRemove = (
  poolingState: PoolingNormalizationToolState,
  index: number
) => {
  const accumulator = {
    harvestWellsToRemove: [] as WorklistWellMappingState[],
    intWellsToRemove: [] as WorklistWellMappingState[],
    destWellsToRemove: [] as WorklistWellMappingState[],
  };

  for (const well of poolingState.sourcePlateInfo[index].wellInfo) {
    const wellId = getPlateWellIdFromRowAndColIndex(
      well.rowPos - 1,
      well.colPos - 1
    );

    const mapping = getFullMappingFromSourceToDestinationPlate(
      poolingState.worklistValues,
      index,
      wellId
    );

    if (mapping && mapping.stampTopLeftTransfers.length > 0) {
      return undefined;
    }

    if (mapping) {
      accumulator.harvestWellsToRemove.push(...mapping.harvestWells);
      accumulator.intWellsToRemove.push(...mapping.intWells);
      accumulator.destWellsToRemove.push(...mapping.destWells);
    }
  }

  return accumulator;
};
